import { useEffect } from "react";
import { Link } from "react-router-dom";
import translate from "../../services/translation.json";
import emailIcon from "../../assets/images/blue-email.svg";
import locationIcon from "../../assets/images/blue-location.svg";
import fbIcon from "../../assets/images/fb-blue.svg";
import instaIcon from "../../assets/images/blue-insta.svg";
import linkedinIcon from "../../assets/images/linkedin-blue.svg";
import "./contactSection.scss";

const CALENDLY_BASE = "https://calendly.com/tech-metamorph-sd/30min";
const CALENDLY_SCRIPT_SRC =
  "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_CSS_HREF =
  "https://assets.calendly.com/assets/external/widget.css";

const buildCalendlyUrl = (lang) => {
  // Keep Calendly light so its internal scrollbar stays readable.
  // Dark chrome is handled by our overlay/popup shell, not Calendly params.
  const params = new URLSearchParams({
    hide_gdpr_banner: "1",
    hide_event_type_details: "0",
    background_color: "ffffff",
    text_color: "1a1a2e",
    primary_color: "189bf5",
    locale: lang === "de" ? "de" : "en",
  });
  return `${CALENDLY_BASE}?${params.toString()}`;
};

const ensureCalendlyAssets = () =>
  new Promise((resolve) => {
    if (!document.querySelector(`link[href="${CALENDLY_CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_CSS_HREF;
      document.head.appendChild(link);
    }

    if (window.Calendly?.initPopupWidget) {
      resolve();
      return;
    }

    let script = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = CALENDLY_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    const done = () => resolve();
    script.addEventListener("load", done, { once: true });

    const poll = setInterval(() => {
      if (window.Calendly?.initPopupWidget) {
        clearInterval(poll);
        resolve();
      }
    }, 100);
  });

const lockPageScroll = (locked) => {
  document.documentElement.classList.toggle("calendly-open", locked);
  document.body.classList.toggle("calendly-open", locked);
};

const polishCalendlyChrome = () => {
  const overlay = document.querySelector(".calendly-overlay");
  if (!overlay) return;

  overlay.style.setProperty("overflow", "hidden", "important");
  overlay.style.setProperty("scrollbar-width", "none", "important");

  const popup = overlay.querySelector(".calendly-popup");
  const content = overlay.querySelector(".calendly-popup-content");
  const iframe = overlay.querySelector("iframe");

  if (popup) {
    popup.style.setProperty("overflow", "hidden", "important");
    popup.style.setProperty("background", "#ffffff", "important");
    popup.style.setProperty("scrollbar-width", "none", "important");
  }

  if (content) {
    content.style.setProperty("overflow", "hidden", "important");
    content.style.setProperty("background", "#ffffff", "important");
    content.style.setProperty("scrollbar-width", "none", "important");
  }

  if (iframe) {
    iframe.style.setProperty("color-scheme", "light", "important");
    iframe.style.setProperty("background", "#ffffff", "important");
    iframe.style.setProperty("scrollbar-color", "#189bf5 #e8eef5", "important");
  }
};

const ContactSection = ({ lang }) => {
  useEffect(() => {
    ensureCalendlyAssets();

    const onMessage = (event) => {
      if (!String(event.origin || "").includes("calendly.com")) return;
      const type = event.data?.event;
      if (type === "calendly.event_type_viewed") {
        lockPageScroll(true);
        polishCalendlyChrome();
      }
      if (
        type === "calendly.profile_page_viewed" ||
        type === "calendly.date_and_time_selected" ||
        type === "calendly.event_scheduled"
      ) {
        lockPageScroll(true);
        polishCalendlyChrome();
      }
    };

    const observer = new MutationObserver(() => {
      const open = Boolean(document.querySelector(".calendly-overlay"));
      lockPageScroll(open);
      if (open) polishCalendlyChrome();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("message", onMessage);

    return () => {
      observer.disconnect();
      window.removeEventListener("message", onMessage);
      lockPageScroll(false);
    };
  }, []);

  const openCalendly = async () => {
    await ensureCalendlyAssets();
    if (window.Calendly?.initPopupWidget) {
      lockPageScroll(true);
      window.Calendly.initPopupWidget({ url: buildCalendlyUrl(lang) });
      requestAnimationFrame(() => polishCalendlyChrome());
      setTimeout(polishCalendlyChrome, 50);
      setTimeout(polishCalendlyChrome, 250);
    } else {
      window.open(buildCalendlyUrl(lang), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-section-inner mw-1200">
        <h2 className="contact-section-title blue center title">
          {translate[lang].contact_page.title}
        </h2>
        <p className="contact-section-subtitle center white">
          {translate[lang].contact_page.subtitle}
        </p>

        <div className="contact-section-content">
          <div className="contact-section-booking">
            <p className="contact-section-booking-kicker">
              {translate[lang].contact_page.book_call}
            </p>
            <h3 className="contact-section-booking-title">
              {translate[lang].contact_page.booking_title}
            </h3>
            <p className="contact-section-booking-text">
              {translate[lang].contact_page.booking_text}
            </p>
            <ul className="contact-section-booking-points">
              {translate[lang].contact_page.booking_points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <button
              type="button"
              className="contact-section-booking-btn"
              onClick={openCalendly}
            >
              {translate[lang].contact_page.booking_cta}
            </button>
          </div>

          <div className="contact-section-info">
            <h3 className="blue">{translate[lang].contact_page.get_in_touch}</h3>
            <div className="contact-details">
              <div>
                <img src={emailIcon} alt="" />
                <a href="mailto:tech@metamorph-sd.com">tech@metamorph-sd.com</a>
              </div>
              <div>
                <img className="location-icon" src={locationIcon} alt="" />
                <span>{translate[lang].footer.address}</span>
              </div>
            </div>

            <div className="follow-us">
              <p>{translate[lang].contact_page.follow}:</p>
              <div className="social-links">
                <Link
                  to="https://www.linkedin.com/company/metamorph-system-development/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={linkedinIcon} alt="LinkedIn" />
                </Link>
                <Link
                  to="https://www.facebook.com/MetamorphSysDev"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={fbIcon} alt="Facebook" />
                </Link>
                <Link
                  to="https://www.instagram.com/metamorphsystemdevelopment/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={instaIcon} alt="Instagram" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
